/**
 * A file's content-search state, quiet when healthy.
 *
 * The design rule: the steady state must not shout. `indexed` renders a bare
 * green check (tooltip explains it) and only by request (`showIndexed`, the
 * list view's Status column) — everywhere else silence means fine. The
 * exceptional states get labelled chips: an animated one while indexing,
 * red for failures, muted for formats content search cannot cover.
 *
 * Null-when-empty like VisibilityBadge, so call sites never need a guard.
 */

import { AlertCircle, CheckCircle2, Clock, FileX, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

const CHIP =
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap';

const STATES = {
  indexing: {
    chip: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
    Icon: Loader2,
    iconClass: 'animate-spin',
  },
  pending: {
    chip: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
    Icon: Clock,
  },
  failed: {
    chip: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
    Icon: AlertCircle,
  },
  unsupported: {
    chip: 'bg-muted text-muted-foreground',
    Icon: FileX,
  },
};

export function IndexStatusBadge({ status, showIndexed = false, className }) {
  const { t } = useTranslation(['my-docs']);

  if (!status) return null;

  if (status === 'indexed') {
    if (!showIndexed) return null;
    return (
      <span
        className={cn('inline-flex items-center', className)}
        title={t('my-docs:fileStatus.hints.indexed')}
        aria-label={t('my-docs:fileStatus.indexed')}
      >
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
      </span>
    );
  }

  const state = STATES[status];
  if (!state) return null;
  const { Icon } = state;
  const labelKey = status === 'pending' ? 'pendingIndex' : status;

  return (
    <span
      className={cn(CHIP, state.chip, className)}
      title={t(`my-docs:fileStatus.hints.${labelKey}`)}
    >
      <Icon className={cn('w-3 h-3', state.iconClass)} />
      <span>{t(`my-docs:fileStatus.${labelKey}`)}</span>
    </span>
  );
}
